/**
 * The sticky right column — the DSCR at each confidence level, coverage
 * against the program floor, the initial-entry baseline, and the four monthly
 * figures behind the ratio.
 *
 * `verifiedDscr` is NOT guaranteed to be ≤ `validatedDscr` — the verified
 * subset can skew either way (drop an unverified expense row and the ratio
 * goes UP) — so nothing here treats one as a ceiling for the other.
 *
 * EQUAL TIERS COLLAPSE (owner feedback 2026-08-25): stated / validated /
 * verified are distinct numbers (raw / haircut / verified-rows-only) but read
 * as redundant when they coincide — all-100% haircuts make stated == validated.
 * Tiers with the same value render as ONE stat with a combined label
 * ("Stated = Validated DSCR") whose tooltip carries each tier's definition;
 * they expand back to separate stats only when they diverge.
 *
 * Every figure is a server field: `statedDscr` / `validatedDscr` /
 * `verifiedDscr` / `clearsFloor` / `programFloor` / `initialEntry` /
 * `monthlyPiPayment` / `pitiaValidated`; the income and expense lines are sums
 * of per-row `validatedMonthlyAmount`.
 */
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { slate, status, teal } from "../../../../theme/colors";

const fmtDscr = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}×`;

const Eyebrow = ({ children, hint }) => (
  <Flx ac g={0.5} sx={{ mb: 0.75 }}>
    <Txt
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: slate[500],
      }}
    >
      {children}
    </Txt>
    {hint ? (
      <Tooltip title={hint}>
        <HelpOutlineOutlined sx={{ fontSize: 14, color: slate[400] }} />
      </Tooltip>
    ) : null}
  </Flx>
);

Eyebrow.propTypes = { children: PropTypes.node, hint: PropTypes.node };

/* ─────────────────────────── the DSCR tier stack ────────────────────────── */

const TIERS = [
  {
    key: "stated",
    label: "Stated",
    field: "statedDscr",
    hint: "Every row at face value — no haircuts, no verification. The optimistic number.",
  },
  {
    key: "validated",
    label: "Validated",
    field: "validatedDscr",
    hint: "Every row after its UW haircut (counted %), verified or not.",
  },
  {
    key: "verified",
    label: "Verified",
    field: "verifiedDscr",
    hint: "Verified rows only — what the lock relies on. Not necessarily lower than validated: dropping an unverified expense raises the ratio.",
  },
];

// The server rounds each tier to 4dp; equal at that precision = one number.
const sameDscr = (a, b) =>
  a != null &&
  b != null &&
  Number.isFinite(Number(a)) &&
  Number.isFinite(Number(b)) &&
  Math.abs(Number(a) - Number(b)) < 0.00005;

/** Group the tiers by value, stated → validated → verified. Nulls never merge. */
const groupTiers = (calc) => {
  const groups = [];
  TIERS.forEach((tier) => {
    const value = calc?.[tier.field];
    const home = groups.find((g) => sameDscr(g.value, value));
    if (home) home.tiers.push(tier);
    else groups.push({ value, tiers: [tier] });
  });
  return groups;
};

const groupHint = (tiers) =>
  tiers.length === 1 ? (
    tiers[0].hint
  ) : (
    <Flx column g={0.75}>
      {tiers.map((t) => (
        <Box key={t.key}>
          <Txt sx={{ display: "block", fontSize: 11.5, fontWeight: 700 }}>{t.label}</Txt>
          <Txt sx={{ fontSize: 11.5 }}>{t.hint}</Txt>
        </Box>
      ))}
    </Flx>
  );

/* ───────────────────────────── program floor ────────────────────────────── */

const FLOOR_PRESETS = [
  { label: "1.00", value: 1, title: "1.00 guideline" },
  { label: "0.80", value: 0.8, title: "0.80 exception" },
];

/** Always-editable: type a floor or tap a preset; Save appears when it differs. */
const ProgramFloorRow = ({ value, onSave, saving }) => {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  useEffect(() => setDraft(value == null ? "" : String(value)), [value]);
  const n = Number(draft);
  const dirty = draft !== "" && Number.isFinite(n) && n !== Number(value);
  const commit = (next) => {
    const v = next == null ? n : next;
    if (!Number.isFinite(v)) return;
    onSave?.(v);
  };
  return (
    <Box>
      <Flx ac g={1} wrap>
        <Txt sx={{ fontSize: 12.5, color: slate[700] }}>Program floor</Txt>
        <TextField
          size="small"
          value={draft}
          placeholder="—"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && dirty) commit();
          }}
          inputProps={{ inputMode: "decimal" }}
          sx={{ width: 68, "& .MuiInputBase-input": { fontSize: 12.5, py: 0.6, textAlign: "center" } }}
        />
        {FLOOR_PRESETS.map((p) => (
          <Tooltip key={p.value} title={p.title}>
            <Chip
              size="small"
              label={p.label}
              variant={Number(value) === p.value ? "filled" : "outlined"}
              onClick={() => commit(p.value)}
              disabled={saving}
              sx={{ height: 24, fontSize: 11.5 }}
            />
          </Tooltip>
        ))}
      </Flx>
      {dirty ? (
        <Button size="small" variant="outlined" onClick={() => commit()} disabled={saving} sx={{ mt: 1 }}>
          Save
        </Button>
      ) : null}
    </Box>
  );
};

ProgramFloorRow.propTypes = {
  value: PropTypes.number,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
};

/* ─────────────────────────────────── rail ───────────────────────────────── */

const Line = ({ label, value, strong }) => (
  <Flx ac jb g={1} sx={{ py: 0.5 }}>
    <Txt sx={{ fontSize: 12.5, color: slate[700] }}>{label}</Txt>
    <Txt sx={{ fontSize: 12.5, fontWeight: strong ? 700 : 600, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Txt>
  </Flx>
);

Line.propTypes = { label: PropTypes.node, value: PropTypes.node, strong: PropTypes.bool };

const DscrSummaryColumn = ({
  calc,
  sourceCount,
  verifiedCount,
  incomeValidated,
  expenseValidated,
  onSaveFloor,
  savingFloor,
}) => {
  const verified = calc?.verifiedDscr;
  const programFloor = calc?.programFloor;
  const clearsFloor = calc?.clearsFloor ?? null;
  const tierGroups = useMemo(() => groupTiers(calc), [calc]);
  const initialEntry = calc?.initialEntry;
  const pct = sourceCount ? Math.round(((verifiedCount || 0) / sourceCount) * 100) : 0;

  return (
    <Box
      sx={{
        border: `1px solid ${slate[200]}`,
        borderRadius: "10px",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {tierGroups.map((g) => {
        const hasVerified = g.tiers.some((t) => t.key === "verified");
        return (
          <Box key={g.tiers.map((t) => t.key).join("=")}>
            <Box sx={{ px: 2.5, py: 2 }}>
              <Eyebrow hint={groupHint(g.tiers)}>
                {g.tiers.map((t) => t.label).join(" = ")} DSCR
              </Eyebrow>
              <Txt
                sx={{
                  display: "block",
                  fontSize: 30,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: hasVerified && g.value != null ? status.success.fg : slate[900],
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtDscr(g.value)}
              </Txt>
              {hasVerified ? (
                <Box sx={{ mt: 1 }}>
                  <Txt sx={{ display: "block", fontSize: 11.5, color: slate[600], mb: 0.5 }}>
                    Verified rows: {verifiedCount ?? 0} / {sourceCount ?? 0}
                  </Txt>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: slate[100],
                      "& .MuiLinearProgress-bar": { bgcolor: status.success.solid, borderRadius: 3 },
                    }}
                  />
                  {calc?.fullyVerified === false ? (
                    <Flx
                      g={0.75}
                      sx={{
                        mt: 1.5,
                        p: 1.25,
                        borderRadius: "8px",
                        bgcolor: status.warning.bg,
                        border: "1px solid #FDE68A",
                        alignItems: "flex-start",
                      }}
                    >
                      <WarningAmberOutlined sx={{ fontSize: 16, color: status.warning.fg, flexShrink: 0 }} />
                      <Txt sx={{ fontSize: 11.5, color: status.warning.fg, lineHeight: 1.45 }}>
                        Not every row is verified — those are excluded from the verified DSCR until UW review.
                      </Txt>
                    </Flx>
                  ) : null}
                </Box>
              ) : null}
            </Box>
            <Divider />
          </Box>
        );
      })}

      {/* coverage */}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Eyebrow>Coverage</Eyebrow>
        <ProgramFloorRow value={programFloor} onSave={onSaveFloor} saving={savingFloor} />
        <Box sx={{ mt: 1.25 }}>
          {clearsFloor == null ? (
            <Txt sx={{ fontSize: 12, color: slate[600], lineHeight: 1.45 }}>
              {programFloor == null
                ? "No floor set — no verdict. Choose 1.00 guideline or 0.80 exception."
                : "No verified DSCR yet — verify rows to get a verdict."}
            </Txt>
          ) : (
            <Flx ac g={0.75}>
              {clearsFloor ? (
                <CheckCircleOutlined sx={{ fontSize: 17, color: status.success.solid }} />
              ) : (
                <WarningAmberOutlined sx={{ fontSize: 17, color: status.error.fg }} />
              )}
              <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: clearsFloor ? status.success.fg : status.error.fg }}>
                {clearsFloor
                  ? `Verified ${fmtDscr(verified)} clears the ${fmtDscr(programFloor)} floor`
                  : `Verified ${fmtDscr(verified)} is below the ${fmtDscr(programFloor)} floor — lock blocked`}
              </Txt>
            </Flx>
          )}
        </Box>

        {initialEntry ? (
          <Box sx={{ mt: 1.75, pt: 1.25, borderTop: `1px dashed ${slate[200]}` }}>
            <Txt sx={{ fontSize: 12.5, color: slate[700] }}>
              Initial entry <b>{fmtDscr(initialEntry.dscr)}</b> → finalized{" "}
              <b style={{ color: verified == null ? slate[600] : status.success.fg }}>{fmtDscr(verified)}</b>
            </Txt>
            <Txt sx={{ display: "block", fontSize: 11, color: slate[500], mt: 0.25 }}>
              baseline captured {formatDateTime(initialEntry.capturedAt, "datetime") || "—"}
            </Txt>
          </Box>
        ) : null}

        <Flx g={0.75} sx={{ mt: 1.5, alignItems: "flex-start" }}>
          <LockOutlined sx={{ fontSize: 14, color: teal[600], flexShrink: 0, mt: "2px" }} />
          <Txt sx={{ fontSize: 11.5, color: slate[500], lineHeight: 1.45 }}>
            At lock the verified DSCR overrides the requested figure and the full workbook snapshots
            onto the lock. Below the floor, the request is blocked.
          </Txt>
        </Flx>
      </Box>

      <Divider />
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Line label="Monthly income (validated)" value={formatDollar(incomeValidated, "—")} />
        <Line label="Monthly expenses (validated)" value={formatDollar(expenseValidated, "—")} />
        <Line label="Monthly P&I" value={formatDollar(calc?.monthlyPiPayment, "—")} />
        <Divider sx={{ my: 0.5 }} />
        <Line label="PITIA (validated)" value={formatDollar(calc?.pitiaValidated, "—")} strong />
      </Box>
    </Box>
  );
};

DscrSummaryColumn.propTypes = {
  calc: PropTypes.object,
  sourceCount: PropTypes.number,
  verifiedCount: PropTypes.number,
  incomeValidated: PropTypes.number,
  expenseValidated: PropTypes.number,
  onSaveFloor: PropTypes.func,
  savingFloor: PropTypes.bool,
};

export default DscrSummaryColumn;
